import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { t, isMobile, throttle } from '@insight/toolkit-utils';
import Button from '../Button/Button';
import TabManager from '../Tabs/TabManager';
import { default as PDPDetails } from './PDPDetails';
import PDPOverview from './PDPOverview';
import PDPTechSpecs from './PDPTechSpecs';
import { makeProductDetailURL } from './PDPHelpers';
import { connectToLocale } from '../Locale/Locale';

export const PDPBody = ({
  currencyCode = 'USD',
  description,
  displayListPrice,
  hasContractSection,
  isLoggedIn,
  isIPSUser,
  materialId,
  manufacturerId,
  mfrName,
  technicalSpecs,
  overview,
  imageUrl,
  manufacturerImage,
  name,
  regular,
  showPrice,
  showStock,
  unlimited,
  showBackOrder,
  price,
  showSaving,
}) => {
  const [isOnMobile, setIsOnMobile] = useState(isMobile());
  useEffect(() => {
    const onResize = throttle(() => {
      setIsOnMobile(isMobile());
    }, 250);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const displayTech = technicalSpecs && technicalSpecs.length > 0;
  const overviewTab = {
    content: <PDPOverview description={description} overview={overview} />,
    id: 'Overview',
    name: t('Overview'),
    className: 'c-cs-mini-pdp__tab',
    'data-testid': 'overview-tab',
  };
  const techSpecsTab = {
    content: <PDPTechSpecs technicalSpecs={technicalSpecs} />,
    id: 'TechSpecs',
    name: t('Tech specifications'),
    className: 'c-cs-mini-pdp__tab',
    'data-testid': 'specs-tab',
  };
  const tabs = displayTech ? [overviewTab, techSpecsTab] : [overviewTab];

  const PDPUrl = makeProductDetailURL({
    materialId,
    manufacturerId,
    mfrName,
    description,
  });

  const renderProductDetailUrl = () => (
    // product detail link renders in details component on mobile
    <span className="c-pdp--viewlink">
      <Button color="link" href={PDPUrl}>
        {t('View full product details')}
      </Button>
    </span>
  );

  const {
    abbreviation,
    contractName,
    contractDiscount,
    displayDiscount,
    webPrice,
    listPrice,
    listPriceInclVat,
    webPriceInclVat,
  } = price || {};

  return (
    <div>
      <PDPDetails
        abbreviation={abbreviation || contractName}
        contractDiscount={contractDiscount}
        displayDiscount={displayDiscount}
        displayListPrice={displayListPrice}
        currencyCode={currencyCode}
        hasContractSection={hasContractSection}
        imageUrl={imageUrl}
        isLoggedIn={isLoggedIn}
        isIPSUser={isIPSUser}
        isOnMobile={isOnMobile}
        listPrice={listPrice}
        webPrice={webPrice}
        priceInclVat={webPriceInclVat || listPriceInclVat}
        manufacturerId={manufacturerId}
        manufacturerImage={manufacturerImage}
        mfrName={mfrName}
        materialId={materialId}
        name={name}
        renderProductDetailUrl={renderProductDetailUrl}
        stock={regular}
        unlimited={unlimited}
        showPrice={showPrice}
        showSaving={showSaving}
        showStock={showStock}
        showBackOrder={showBackOrder}
      />
      {!isOnMobile && renderProductDetailUrl()}
      <TabManager className="c-cs-mini-pdp__tabs" tabs={tabs} />
    </div>
  );
};

PDPBody.propTypes = {
  abbreviation: PropTypes.string,
  contractDiscount: PropTypes.number,
  currencyCode: PropTypes.string,
  description: PropTypes.string.isRequired,
  displayListPrice: PropTypes.bool,
  hasContractSection: PropTypes.bool,
  isIPSUser: PropTypes.bool,
  manufacturerId: PropTypes.string.isRequired,
  mfrName: PropTypes.string.isRequired,
  materialId: PropTypes.string.isRequired,
  overview: PropTypes.string,
  technicalSpecs: PropTypes.arrayOf(PropTypes.object).isRequired,
  showPrice: PropTypes.bool,
  showStock: PropTypes.bool,
  showBackOrder: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  price: PropTypes.object,
};

PDPBody.defaultProps = {
  displayListPrice: false,
  hasContractSection: false,
  isIPSUser: false,
  overview: '',
  showPrice: false,
  showStock: true,
  showBackOrder: false,
  isLoggedIn: true,
};

export default connectToLocale(PDPBody);
